import { defineStore } from "pinia";
import { ref } from "vue";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import { createNotification } from "@/tools/notification";
import QZ_TRAY_API from "@/api/classes/qzTray/qzTray_api";
import type { LabelTemplate } from "@/stores/labelTemplate/models/label-template";

export const useQzTrayStore = defineStore("qzTrayStore", () => {
  const printers = ref<string[]>([]);
  const printersForSelect = ref<{ label: string; value: string | null }[]>([
    { value: null, label: "Не указан" },
  ]);
  const printersLoadingStatus = ref(enumStatusLoad.NOT_STARTED);
  const qzTray = ref();
  const qzTrayConnected = ref<boolean>(false);

  const connectToQzTray = async () => {
    if (qzTrayConnected.value) return true;

    qzTrayConnected.value = false;

    try {
      printersLoadingStatus.value = enumStatusLoad.LOADING;
      qzTray.value = (await import("qz-tray")).default;

      const certificate = await QZ_TRAY_API.getKey();

      await qzTray.value.security.setCertificatePromise(
        (
          resolve: (value: string) => void,
          reject: (reason?: string) => void,
        ) => {
          try {
            resolve(certificate.data);
          } catch (err) {
            reject("Ошибка загрузки сертификата");
          }
        },
      );

      await qzTray.value.security.setSignatureAlgorithm("SHA512");
      await qzTray.value.security.setSignaturePromise((toSign: string) => {
        return async (
          resolve: (value: string) => void,
          reject: (reason?: string) => void,
        ) => {
          try {
            const response = await QZ_TRAY_API.signMessage(toSign);
            resolve(response.data);
          } catch (err) {
            reject(`Ошибка подписи: ${err}`);
          }
        };
      });

      await qzTray.value.websocket.connect();
      printers.value = await qzTray.value.printers.find();

      printersForSelect.value = [
        ...new Set([
          ...printersForSelect.value,
          ...printers.value.map((el) => ({ value: el, label: el })),
        ]),
      ];

      qzTrayConnected.value = true;
      printersLoadingStatus.value = enumStatusLoad.LOADED;
      return true;
    } catch (err) {
      createNotification("error", "Ошибка подключения", String(err));
      printersLoadingStatus.value = enumStatusLoad.ERROR;
      return false;
    }
  };

  const disconnectFromQzTray = async () => {
    try {
      if (qzTray.value && qzTray.value.websocket.isActive()) {
        await qzTray.value.websocket.disconnect();
        console.log("Отключение от QZ Tray выполнено.");
      }
    } catch (err) {
      console.error("Ошибка при отключении от QZ Tray:", err);
    }
  };

  const printLabelTemplate = async (
    labelTemplate: LabelTemplate,
    count?: number,
    printerName?: string,
  ) => {
    if (!qzTrayConnected.value) {
      await connectToQzTray();
    }

    const label = labelTemplate.label;

    const widthInches = label.labelWidthInMm / 25.4;
    const heightInches = label.labelHeightInMm / 25.4;
    const ratio = (label.labelHeightInMm * 100) / label.labelWidthInMm;
    const isLandscape = ratio < 65;

    const printer = printerName || label.attachedPrinterName;

    if (!printer) {
      createNotification("warning", "Укажите принтер");
      return;
    }

    const config = qzTray.value.configs.create(printer, {
      copies: count && count !== 1 ? count : label.countOfCopies || 1,
      orientation: isLandscape ? "landscape" : "portrait",
    });

    const base64ImageData = labelTemplate.canvasRef.toDataURL("image/png", 1.0);

    const data = [
      {
        type: "pixel",
        format: "image",
        data: base64ImageData,
        density: labelTemplate.dpiLabel,
        width: widthInches,
        height: heightInches,
      },
    ];

    try {
      await qzTray.value.print(config, data);
      createNotification("success", "Печать завершена");
      return true;
    } catch (err) {
      createNotification("error", "Ошибка печати", String(err));
      return false;
    }
  };

  const getQzTrayInstaller = async () => {
    try {
      const response = await QZ_TRAY_API.getInstaller();
      return response.data;
    } catch (error) {
      console.error("Ошибка при получении установщика QZ Tray:", error);
      throw error;
    }
  };

  return {
    printers,
    printersForSelect,
    qzTrayConnected,
    printersLoadingStatus,
    connectToQzTray,
    disconnectFromQzTray,
    printLabelTemplate,
    getQzTrayInstaller,
  };
});
